import { onCleanup, onMount } from 'solid-js';

export interface ShortcutOptions {
  allowInInputs?: boolean;
  preventDefault?: boolean;
  description?: string;
}

interface Binding {
  combo: string;
  handler: (event: KeyboardEvent) => void;
  options: ShortcutOptions;
}

const bindings: Binding[] = [];

const normalizeKey = (event: KeyboardEvent): string => {
  const parts: string[] = [];
  if (event.metaKey) parts.push('cmd');
  if (event.ctrlKey) parts.push('ctrl');
  if (event.altKey) parts.push('alt');
  if (event.shiftKey) parts.push('shift');
  const key = event.key?.toLowerCase() ?? '';
  if (key && !['control', 'shift', 'alt', 'meta'].includes(key)) parts.push(key);
  return parts.join('+');
};

const isTypingTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return false;
};

const dispatch = (event: KeyboardEvent) => {
  const typed = normalizeKey(event);
  const typing = isTypingTarget(event.target);
  for (const binding of bindings) {
    if (binding.combo !== typed) continue;
    if (typing && !binding.options.allowInInputs) continue;
    if (binding.options.preventDefault) event.preventDefault();
    binding.handler(event);
    return;
  }
};

let listenerAttached = false;
const ensureGlobalListener = () => {
  if (listenerAttached || typeof window === 'undefined') return;
  window.addEventListener('keydown', dispatch);
  listenerAttached = true;
};

export const useShortcut = (
  combo: string | string[],
  handler: (event: KeyboardEvent) => void,
  options: ShortcutOptions = {},
): void => {
  const combos = Array.isArray(combo) ? combo : [combo];
  onMount(() => {
    ensureGlobalListener();
    const added = combos.map<Binding>((c) => ({ combo: c.toLowerCase(), handler, options }));
    bindings.push(...added);
    onCleanup(() => {
      for (const binding of added) {
        const idx = bindings.indexOf(binding);
        if (idx !== -1) bindings.splice(idx, 1);
      }
    });
  });
};

export interface DocumentedShortcut {
  combo: string;
  description: string;
  category: 'global' | 'navigation' | 'schedule' | 'forms';
}

export const DOCUMENTED_SHORTCUTS: DocumentedShortcut[] = [
  { combo: '⌘K', description: 'Открыть командную палитру', category: 'global' },
  { combo: '⌘/', description: 'Показать горячие клавиши', category: 'global' },
  { combo: '/', description: 'Сфокусироваться на поиске', category: 'global' },
  { combo: 'R', description: 'Обновить данные', category: 'global' },
  { combo: 'Esc', description: 'Закрыть модальное окно', category: 'global' },
  { combo: '⌘⇧L', description: 'Переключить тему', category: 'global' },
  { combo: '←/→', description: 'Предыдущий / следующий день в расписании', category: 'schedule' },
  { combo: 'T', description: 'К текущей дате', category: 'schedule' },
  { combo: '+/-', description: 'Увеличить / уменьшить масштаб', category: 'schedule' },
  { combo: '⌘Z', description: 'Отменить действие', category: 'schedule' },
  { combo: '⌘⇧Z', description: 'Повторить действие', category: 'schedule' },
];
